import {
  DEFAULT_GATE_REVIEW_MILESTONES,
  DEFAULT_LAUNCH_MILESTONES,
  STATIC_GR_ACTION_STATUS_VALUES,
  STATIC_MILESTONES,
} from '@gp/constants/const';
import { BoolFlag } from '@gp/enums/common';
import { MilestoneDateStatus } from '@gp/enums/milestone';
import {
  GRANULARITY_UNITS,
  UPLOAD_STATUS_ICONS,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/constants';
import { ScheduleStatus } from '@gp/pages/Pipeline/ProjectDetails/Milestone/enums';
import {
  addMockIdBatch,
  formatDateStr,
  generateMockId,
  getLastDayOfMonth,
  isEmpty,
  isNullOrUndefined,
} from '@gp/utils/common';
import { extractTextFromHtml } from '@home/utils/common';
import moment from 'moment';

import type {
  IGranularity,
  IMilestone,
  IMilestoneMap,
  IUploadStatus,
  IUploadStatusUIData,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import type { IMilestoneReq } from '@gp/services/milestone';
import type {
  IMilestoneFinancialForecastDTO,
  IMilestoneFinancialForecastServerDTO,
  IMilestoneFinancialForecastVO,
  IMilestoneFinancialVO,
} from '@gp/types/milestones';
import type { FieldRenderProps } from 'react-final-form-hooks/dist';
import type { SelectOption } from 'tea-component';

export const getCurrentTime = () => moment().format('YYYY-MM-DD HH:mm:ss');

export const getFileNameFromPath = (path?: string): string => {
  if (!path) return '';
  return path?.split('fileName=')?.[1] || '';
};

export const getUploadStatusUIData = (status: IUploadStatus): IUploadStatusUIData =>
  // 返回Loading状态信息作为兜底
  UPLOAD_STATUS_ICONS[status] || UPLOAD_STATUS_ICONS[4];

export const formatForecastFinancialsReq = (
  list?: IMilestoneFinancialForecastVO[] | null,
): IMilestoneFinancialForecastServerDTO[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const {
      team,
      // REMARK: 注意这里虽然方法下面代码没用到mock_id字段，但是这里是为了让下面的metrics不要取到mock_id字段
      mock_id: mockId,
      ...metrics
    } = item || {};
    const details = Object.entries(metrics || {}).map((metric) => {
      const [metricKey, metricValue] = metric || [];
      return { name: metricKey, value: metricValue };
    });
    return { team, details };
  });
};

const formatForecastFinancialsRes = (
  list?: IMilestoneFinancialForecastServerDTO[] | null,
): IMilestoneFinancialForecastVO[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const { team, details } = item || {};
    const metrics: Omit<IMilestoneFinancialForecastDTO, 'team'> = {
      net_profit_ratio: '',
      y1_sales: '',
      y1_copies: '',
      y3_sales: '',
      y3_copies: '',
    };
    if (Array.isArray(details) && details.length > 0) {
      details.forEach((detail) => {
        const { name, value } = detail || {};
        // @ts-ignore
        metrics[name] = value;
      });
    }
    return { team, mock_id: generateMockId(), ...metrics };
  });
};

export const formatResMilestones = (milestones?: any, parentMilestoneId = ''): IMilestone[] => {
  if (!Array.isArray(milestones) || !milestones.length) return [];
  return milestones.map((milestone) => {
    // 这里不知道为啥 接口返回的父Milestone有update_time字段，没有updatetime字段，子Milestone有updatetime字段，没有update_time字段，下面两个字段都采用
    const {
      date,
      gate_review: gateReview,
      milestone_gate_review: milestoneGateReview,
      key_milestones_financial: keyMilestonesFinancials,
      key_milestones_financial_forecast: keyMilestonesFinancialsForecast,
      action_items: actionItems,
      material: materials,
      update_time: updateTime1,
      updatetime: updateTime2,
      is_del: isDel,
      modify_personnel: modifyPersonnel,
      create_time: createTime,
    } = milestone || {};
    const formattedActionItems = actionItems?.map((actionItem: any) => {
      const { id } = actionItem || {};
      let { state } = actionItem || {};
      state = state === undefined || state === null ? '' : String(state);
      state = STATIC_GR_ACTION_STATUS_VALUES[state?.toUpperCase()] || state || '';
      return { ...actionItem, id: generateMockId(id), state };
    });
    const formattedMaterials = materials?.map((material: any) => {
      const { path, name } = material || {};
      const fileName = name || getFileNameFromPath(path);
      return { ...material, name: fileName };
    });
    const formattedKeyMilestonesFinancials = addMockIdBatch(keyMilestonesFinancials);
    const formattedkeyMilestonesFinancialsForecast = formatForecastFinancialsRes(
      keyMilestonesFinancialsForecast,
    );
    const updateTimeCommon = createTime || updateTime1 || updateTime2 || '';
    const {
      due_date: dueDate,
      comment,
      grid,
      review_type: reviewType,
      score,
      stage,
      status,
    } = milestoneGateReview || {};
    const milestoneGateReviewCommon = milestoneGateReview || {
      due_date: dueDate || '',
      comment: comment || '',
      grid: grid || null,
      review_type: reviewType || null,
      score: score || null,
      stage: stage || null,
      status: status || null,
    };
    return {
      ...milestone,
      parent_milestone_id: parentMilestoneId,
      date: formatDateStr(date, 'YYYY-MM-DD'),
      gate_review: isNullOrUndefined(gateReview) || gateReview === 0 ? '' : String(gateReview),
      milestone_gate_review: milestoneGateReviewCommon,
      key_milestones_financial: formattedKeyMilestonesFinancials || [],
      key_milestones_financial_forecast: formattedkeyMilestonesFinancialsForecast || [],
      action_items: formattedActionItems || [],
      material: formattedMaterials || [],
      is_del: isDel || 0,
      modify_personnel: modifyPersonnel || '',
      update_time: updateTimeCommon || '',
      updatetime: updateTimeCommon || '',
      create_time: createTime || '',
    };
  });
};

/**
 * 组装Milestone的下拉选项options集合
 * 选项为STATIC_MILESTONES常量配置的options集合
 * 禁用已存在的Milestone名称的选项
 * @param milestones 已存在的milestones集合
 * @return Milestone下拉选项options集合
 */
export const formatMilestoneOptions = (milestones?: IMilestone[]): SelectOption[] => {
  const milestoneNames = milestones?.map((milestone) => milestone?.milestone);
  return STATIC_MILESTONES.map((option) => ({
    ...option,
    disabled: milestoneNames?.includes(option.value),
  }));
};

/**
 * 判断对象数组 至少有一个对象（该对象至少有一个非空字段，excludes指定的字段除外）
 * excludes集合中指定的字段 不参与判断
 * @param list 对象数组
 * @param excludes 排除字段
 * @returns 对象数组是否存在至少一个对象（该对象至少包含一个非空字段）
 */
export const getIsNotEmptyList = (list?: Record<string, any>[], excludes?: string[]): boolean => {
  if (!Array.isArray(list) || !list.length) return false;
  for (const item of list) {
    if (!item) continue;
    const entries = Object.entries(item);
    for (const entry of entries) {
      const [key, value] = entry || [];
      if (!key) continue;
      if (excludes?.includes(key)) continue;
      if (!!value) return true;
    }
  }
  return false;
};

/**
 * 根据Milestons集合、currentMilestoneId 获取currentMilestone、nextMilestone
 * @param milestones Milestones集合
 * @param currentMilestoneId currentMilestone的milestone_id
 * @returns currentMilestone、nextMilestone
 */
const getKeyMilestonesByCurrentMilestoneId = (
  milestones?: IMilestone[],
  currentMilestoneId?: string,
): {
  currentMilestone: IMilestone | null;
  nextMilestone: IMilestone | null;
} => {
  if (!Array.isArray(milestones) || !milestones.length)
    return { currentMilestone: null, nextMilestone: null };
  const currentMilestoneIdx = milestones.findIndex(
    (milestone) => milestone?.milestone_id === currentMilestoneId,
  );
  const nextMilestoneIdx =
    currentMilestoneIdx === -1 ? -1 : Math.min(currentMilestoneIdx + 1, milestones.length - 1);
  const currentMilestone = milestones[currentMilestoneIdx];
  const nextMilestone = milestones[nextMilestoneIdx];
  return { currentMilestone, nextMilestone };
};

/**
 * 根据Milestons集合、nextMilestoneId 获取currentMilestone、nextMilestone
 * @param milestones Milestones集合
 * @param nextMilestoneId nextMilestone的milestone_id
 * @returns currentMilestone、nextMilestone
 */
const getKeyMilestonesByNextMilestoneId = (
  milestones?: IMilestone[],
  nextMilestoneId?: string,
): {
  currentMilestone: IMilestone | null;
  nextMilestone: IMilestone | null;
} => {
  if (!Array.isArray(milestones) || !milestones.length)
    return { currentMilestone: null, nextMilestone: null };
  const nextMilestoneIdx = milestones.findIndex(
    (milestone) => milestone?.milestone_id === nextMilestoneId,
  );
  const currentMilestoneIdx = nextMilestoneIdx === -1 ? -1 : Math.max(nextMilestoneIdx - 1, 0);
  const currentMilestone = milestones[currentMilestoneIdx];
  const nextMilestone = milestones[nextMilestoneIdx];
  return { currentMilestone, nextMilestone };
};

/**
 * 根据Milestons集合、currentMilestoneId、nextMilestoneId 获取currentMilestone、nextMilestone
 * 优先用nextMilestoneId 反推 currentMilestone、没有nextMilestoneId有currentMilestoneId时，再用currentMilestone推nextMilestone
 * @param milestones Milestones集合
 * @param currentMilestoneId currentMilestone的milestone_id
 * @param nextMilestoneId nextMilestone的milestone_id
 * @returns currentMilestone、nextMilestone
 */
export const getKeyMilestones = (
  milestones?: IMilestone[],
  currentMilestoneId?: string,
  nextMilestoneId?: string,
): {
  currentMilestone: IMilestone | null;
  nextMilestone: IMilestone | null;
} => {
  if (nextMilestoneId) return getKeyMilestonesByNextMilestoneId(milestones, nextMilestoneId);
  if (currentMilestoneId)
    return getKeyMilestonesByCurrentMilestoneId(milestones, currentMilestoneId);
  return { currentMilestone: null, nextMilestone: null };
};

/**
 * 判定milestone的financials集合数据 是否为有效数据
 * @param milestoneFinancials milestone的financials集合数据
 * @returns milestone的financials集合数据 是否为有效数据
 */
export const getHasGRMilestoneFinancialsData = (
  milestoneFinancials?: IMilestoneFinancialVO[] | null,
): boolean => {
  if (!Array.isArray(milestoneFinancials) || !milestoneFinancials.length) return false;
  return milestoneFinancials.some((milestoneFinancial) => {
    const { milestone, dev_cost: devCost, dev_hc: devHc } = milestoneFinancial || {};
    return !isEmpty(milestone) && (!isEmpty(devCost) || !isEmpty(devHc));
  });
};

export const getHasGRMilestoneFinancialsForecastData = (
  milestoneFinancialsForecast?: IMilestoneFinancialForecastVO[] | null,
): boolean => {
  if (!Array.isArray(milestoneFinancialsForecast) || !milestoneFinancialsForecast.length)
    return false;
  return milestoneFinancialsForecast.some((milestoneFinancialForecast) => {
    const {
      team,
      net_profit_ratio: netProfitRatio,
      y1_sales: y1Revenue,
      y1_copies: y1CopySales,
      y3_sales: y3Revenue,
      y3_copies: y3CopySales,
    } = milestoneFinancialForecast || {};
    return (
      !isEmpty(team) &&
      (!isEmpty(netProfitRatio) ||
        !isEmpty(y1Revenue) ||
        !isEmpty(y1CopySales) ||
        !isEmpty(y3Revenue) ||
        !isEmpty(y3CopySales))
    );
  });
};

/**
 * 判断指定Milestone是否有GR数据（编辑过GR）
 * @param milestone Milestone
 * @returns 是否有GR数据，即编辑过GR
 */
export const getHasGRData = (milestone?: IMilestone): boolean => {
  const {
    action_items: milestoneActionItems,
    gate_review_decision: milestoneGateReviewDecision,
    gate_review: milestoneGateReview,
    key_feedback: milestoneKeyFeedback,
    dev_cost_actual: milestoneDevCostActual,
    dev_cost_actual_history: milestoneDevCostActualHistories,
    approved_budget_to_next: milestoneApprovedBudgetToNext,
    approved_budget_to_next_history: milestoneApprovedBudgetToNextHistories,
    dev_cost: milestoneDevCost,
    dev_cost_history: milestoneDevCostHistories,
    key_milestones_financial: milestoneKeyMilestonesFinancials,
    key_milestones_financial_forecast: milestoneKeyMilestonesFinancialsForecast,
    next_milestone_scope: milestoneNextMilestoneScope,
    material: milestoneMaterials,
    budget_next_milestone: milestoneBudgetNextMilestone,
  } = milestone || {};

  const hasGateReview =
    (!!milestoneGateReview && milestoneGateReview !== '0') ||
    !!extractTextFromHtml(milestoneGateReviewDecision);
  const hasKeyFeedback = !!extractTextFromHtml(milestoneKeyFeedback);
  const hasDevCostActual = !!milestoneDevCostActual;
  const hasDevCostActualHistories =
    Array.isArray(milestoneDevCostActualHistories) && milestoneDevCostActualHistories.length > 0;

  const hasApprovedBudgetToNext = !!milestoneApprovedBudgetToNext;
  const hasApprovedBudgetToNextHistories =
    Array.isArray(milestoneApprovedBudgetToNextHistories) &&
    milestoneApprovedBudgetToNextHistories.length > 0;
  const hasApprovedBudgetToNextMilestone = !!milestoneBudgetNextMilestone;

  const hasDevCost = !!milestoneDevCost;
  const hasDevCostHistories =
    Array.isArray(milestoneDevCostHistories) && milestoneDevCostHistories.length > 0;

  const hasMilestoneKeyMilestonesFinancials = getHasGRMilestoneFinancialsData(
    milestoneKeyMilestonesFinancials,
  );
  const hasMilestoneKeyMilestonesFinancialsForecast = getHasGRMilestoneFinancialsForecastData(
    milestoneKeyMilestonesFinancialsForecast,
  );

  const hasActionItems = Array.isArray(milestoneActionItems) && milestoneActionItems.length > 0;
  const hasNextMilestoneScope = !!extractTextFromHtml(milestoneNextMilestoneScope);
  const hasMaterials = Array.isArray(milestoneMaterials) && milestoneMaterials.length > 0;

  return (
    hasGateReview ||
    hasKeyFeedback ||
    hasDevCostActual ||
    hasDevCostActualHistories ||
    hasApprovedBudgetToNext ||
    hasApprovedBudgetToNextHistories ||
    hasApprovedBudgetToNextMilestone ||
    hasDevCost ||
    hasDevCostHistories ||
    hasMilestoneKeyMilestonesFinancials ||
    hasMilestoneKeyMilestonesFinancialsForecast ||
    hasActionItems ||
    hasNextMilestoneScope ||
    hasMaterials
  );
};

/**
 * 获取Milestone的进度状态，根据日期的提前、延后 展示不通的状态
 * @param diff 最新日期与原始日期间的差值，大于0为延后，小于0为提前
 * @return Milestone的进度状态
 */
const getScheduleStatus = (diff: number): ScheduleStatus => {
  if (Number.isNaN(diff)) return ScheduleStatus.Normal;
  if (diff === 0) return ScheduleStatus.Normal;
  if (diff > 0) return ScheduleStatus.Delay;
  return ScheduleStatus.Advance;
};

/**
 * 计算当前日期与原始日期间的差值
 * @param params 参数对象
 * @param params.granularity 时间粒度
 * @param params.originalDate 原始日期
 * @param params.currentDate 当前日期
 * @return 当前日期与原始日期间的差值
 */
export const getDateDiff = (params: {
  granularity?: IGranularity;
  originalDate?: string;
  currentDate?: string;
}): number => {
  const { granularity, originalDate, currentDate } = params || {};
  const granularityUnit = granularity ? GRANULARITY_UNITS[granularity] || 'days' : 'days';
  const originalDateM: moment.Moment | null = originalDate ? moment(originalDate) : null;
  const currentDateM: moment.Moment | null = currentDate ? moment(currentDate) : null;
  if (!originalDateM?.isValid()) return 0;
  if (!currentDateM?.isValid()) return 0;
  return currentDateM.diff(originalDateM, granularityUnit);
};

/**
 * 获取Milestone的进度状态信息，包含status、num，分别表示提前、延后、正常，天数（提前、延后）
 * @param params 参数对象
 * @param params.milestoneMap Milestone_id与milestone的映射
 * @param params.milestoneId Milestone的milestone_id
 * @param params.milestoneDate Milestone的最新日期
 * @return Milestone的进度状态
 */
export const getMilestoneAdvanceDelayData = (params: {
  milestoneMap?: IMilestoneMap;
  milestoneId?: string;
  milestoneGranularity?: IGranularity;
  milestoneDate?: string;
}): {
  status: ScheduleStatus;
  granularity: IGranularity;
  num: number;
} => {
  const { milestoneMap, milestoneId, milestoneGranularity, milestoneDate } = params || {};
  const originalMilestone = milestoneMap?.[milestoneId as string];
  const originalMilestoneDate = originalMilestone?.date;
  const diff = getDateDiff({
    granularity: milestoneGranularity,
    originalDate:
      milestoneGranularity === 'monthly'
        ? getLastDayOfMonth(originalMilestoneDate)
        : originalMilestoneDate,
    currentDate:
      milestoneGranularity === 'monthly' ? getLastDayOfMonth(milestoneDate) : milestoneDate,
  });
  return {
    status: getScheduleStatus(diff),
    granularity: milestoneGranularity || 'daily',
    num: Math.abs(diff),
  };
};

/**
 * 创建Milestone的milestone_id与Milestone的映射
 * @param milestones Milestones集合
 * @return Milestone的milestone_id与Milestone的映射
 */
export const getMilestoneMap = (milestones?: IMilestone[]): Record<string, IMilestone> => {
  if (!Array.isArray(milestones) || !milestones.length) return {};
  const map: Record<string, IMilestone> = {};
  milestones.forEach((milestone) => {
    const { milestone_id: milestoneId } = milestone || {};
    map[milestoneId] = milestone;
  });
  return map;
};

/**
 * 递归同步Milestone的date字段
 * 该方法为synchromizeMilestonesDateChange的内部方法，所以参数没做校验了，应为在synchromizeMilestonesDateChange方法内已经做了参数校验
 * @param params 参数对象
 * @param params.milestones 要同步date字段的Milestones集合
 * @param params.synchronizeMilestoneIds 要同步date字段的Milestones的milestone_id集合
 * @param params.unit 时间粒度单位
 * @param params.diff 时间差值
 * @return 同步date后的Milestones集合
 */
const synchromizeMilestonesDateChangeInner = (params: {
  milestoneMap: IMilestoneMap;
  milestones: IMilestone[];
  synchronizeMilestoneIds: string[];
  unit: 'days' | 'weeks' | 'months';
  diff: number;
}): IMilestone[] => {
  const { milestoneMap, milestones, synchronizeMilestoneIds, unit, diff } = params;
  return milestones.map((milestone) => {
    const { milestone_id: milestoneId } = milestone || {};
    const originalMilestone = milestoneMap?.[milestoneId];
    const { date: originalMilestoneDate, date_confirm: originalMilestoneDateConfirm } =
      originalMilestone || {};
    const milestoneTmp = { ...milestone };

    if (synchronizeMilestoneIds.includes(milestoneId)) {
      let milestoneDateM: moment.Moment | null = originalMilestoneDate
        ? moment(originalMilestoneDate)
        : null;
      if (milestoneDateM?.isValid()) {
        // 同步date变化
        milestoneDateM =
          diff > 0 ? milestoneDateM.add(diff, unit) : milestoneDateM.subtract(Math.abs(diff), unit);
        // TBC，需要设置为当月的最后一天
        if (originalMilestoneDateConfirm === MilestoneDateStatus.ToBeConfirm) {
          milestoneDateM.endOf('month');
        }
        milestoneTmp.date = milestoneDateM.format('YYYY-MM-DD');
      }
    }

    return milestoneTmp;
  });
};

/**
 * 同步Milestones集合的date字段更新
 * 内部递归调用synchromizeMilestonesDateChangeInner方法进行同步更新date
 * @param params 参数对象
 * @param params.granularity 时间粒度
 * @param params.synchronizeMilestoneIds 要同步date字段的Milestones的milestone_id集合
 * @param params.currentMilestoneId 当前Milestone的milestone_id
 * @param params.currentMilestoneDate 当前Milestone的date
 * @param params.currentMilestones 当前Milestone集合
 * @param params.milestoneMap Milestones映射，键为Milestone的milestone_id，值为Milestone
 * @return 同步date后的Milestones集合
 */
export const synchromizeMilestonesDateChange = (params: {
  granularity?: IGranularity;
  synchronizeMilestoneIds?: string[];
  currentMilestoneId?: string;
  currentMilestoneDate?: string;
  currentMilestones?: IMilestone[];
  milestoneMap?: Record<string, IMilestone>;
}): IMilestone[] => {
  const {
    granularity,
    synchronizeMilestoneIds,
    currentMilestoneId,
    currentMilestoneDate,
    currentMilestones,
    milestoneMap,
  } = params || {};
  const granularityUnit = granularity ? GRANULARITY_UNITS[granularity] || 'days' : 'days';
  if (!Array.isArray(currentMilestones) || !currentMilestones.length) return [];
  if (!Array.isArray(synchronizeMilestoneIds) || !synchronizeMilestoneIds.length)
    return [...currentMilestones];
  if (!milestoneMap) return [...currentMilestones];
  if (!currentMilestoneId) return [...currentMilestones];

  // 当前正在编辑的Milestone的最新date与原始date的差值（同步的数值依据）
  const diff = getDateDiff({
    granularity,
    // 当前正在编辑的Milestone的原始date
    originalDate: milestoneMap[currentMilestoneId]?.date,
    currentDate: currentMilestoneDate,
  });
  // if (diff === 0) return [...currentMilestones];

  return synchromizeMilestonesDateChangeInner({
    milestoneMap,
    milestones: currentMilestones,
    synchronizeMilestoneIds,
    unit: granularityUnit,
    diff,
  });
};

/**
 * 删除Milestone时，重新计算Project的Next Mileston Id（删除的是Project的Next Milestone时，需要重新计算）
 * @param params 参数对象
 * @param params.milestones Milestone集合
 * @param params.nextMilestoneId Project当前Next Milestone的id
 * @param params.deleteMilestoneId 当前删除的Milestone的id
 * @returns 本次删除后Project的Next Milestone Id
 */
export const calcNextMilestoneId = (params?: {
  milestones?: IMilestone[] | null;
  nextMilestoneId?: string;
  deleteMilestoneId?: string;
}): string => {
  const { milestones, nextMilestoneId, deleteMilestoneId } = params || {};
  if (!Array.isArray(milestones) || !milestones.length) return '';
  if (!nextMilestoneId) return '';
  const milestonesLength = milestones.length;
  // 仅有一个Milestones，删除Milestone后，Next Milestone、Current Milestone都为空
  if (milestonesLength === 1) return '';
  // 删除的不是Next Milestone时，直接返回当前Next Milestone Id
  if (nextMilestoneId !== deleteMilestoneId) return nextMilestoneId;
  const deleteMilestoneIdx = milestones.findIndex(
    (milestone) => milestone?.milestone_id === deleteMilestoneId,
  );
  // 数据异常，返回当前Next Milestone Id
  if (deleteMilestoneIdx === -1) return nextMilestoneId;
  // 新Next Milestone的索引
  let newNextMilestoneIdx = -1;
  if (deleteMilestoneIdx === milestonesLength - 1) {
    /**
     * 当删除的Milestone为最后一个Milestone时，Next Milestone往前推一个
     */
    newNextMilestoneIdx = deleteMilestoneIdx - 1;
  } else if (deleteMilestoneIdx === 0) {
    /**
     * 当前删除的Milestone为第一个Milestone时，Next Milestone都调整为第二个Milestone
     */
    newNextMilestoneIdx = 1;
  } else {
    /**
     * 当删除的Milestone位于中间位置时，Next Milestone往后推一个
     */
    newNextMilestoneIdx = deleteMilestoneIdx + 1;
  }
  return milestones[newNextMilestoneIdx]?.milestone_id || nextMilestoneId;
};

// 编辑的milestoneList给没有milestone_id的加上对应id
export const getCorrectMilestoneList = (
  sourceList?: IMilestoneReq[],
  reqList?: IMilestoneReq[],
) => {
  const resList = sourceList;

  if (Array.isArray(sourceList) && Array.isArray(reqList)) {
    for (let i = 0; i < sourceList?.length; i++) {
      const t = sourceList?.[i];
      // 第一层没有milestone_id的对比加上milestone_id
      if (!t?.milestone_id) {
        for (let ii = 0; ii < reqList?.length; ii++) {
          const tt = reqList?.[ii];
          if (t?.milestone === tt?.milestone && t?.start_date === tt?.start_date) {
            t.milestone_id = tt?.milestone_id;
          }
        }
      }
    }
  }

  return resList;
};

/**
 * 判定milestone是否默认为GR（默认为GR，用户可以手动改为GR、非GR）
 * @param milestoneName milestone名称
 * @returns milestone是否默认为GR
 */
export const getMilestoneIsGRDefault = (milestoneName?: string): BoolFlag => {
  return milestoneName && DEFAULT_GATE_REVIEW_MILESTONES.includes(milestoneName)
    ? BoolFlag.True
    : BoolFlag.False;
};

/**
 * 判定milestone是否默认为Launch（默认为Launch，用户可以手动改为Launch、非Launch）
 * @param milestoneName milestone名称
 * @returns milestone是否默认为Launch
 */
export const getMilestoneIsLaunchDefault = (milestoneName?: string): BoolFlag => {
  return milestoneName && DEFAULT_LAUNCH_MILESTONES.includes(milestoneName)
    ? BoolFlag.True
    : BoolFlag.False;
};

/**
 * 增强milestone字段的change handler
 * 1、更新milestone字段的value
 * 2、根据milestone的value，获取milestone对应的默认是否为gr，并更新is_gr字段的value
 * @param milestoneField milestone字段的FieldRenderProps
 * @param isGRField is_gr字段的FieldRenderProps
 * @returns milestone字段的change handler
 */
export const enhanceMilestoneFieldChangeHandler: (
  milestoneField?: FieldRenderProps,
  categoryField?: FieldRenderProps,
  milestoneCategory?: any,
) => (payload: string) => void =
  (milestoneField, categoryField, milestoneCategory) => (payload) => {
    // const milestoneIsGRDefault = getMilestoneIsGRDefault(payload);
    // const milestoneIsLaunchDefault = getMilestoneIsLaunchDefault(payload);
    milestoneField?.input?.onChange(payload);
    categoryField?.input?.onChange(milestoneCategory?.[payload] || '');
  };

// 获取milestone会议活动对应状态
export const getMeetingStatusIcon = (stats: number) => {
  switch (stats) {
    case 2:
      return 'cancelled';
    case 3:
      return 'done';
    case 4:
      return 'planned';
    case 1:
    default:
      return '';
  }
};
